import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import DeleteIcon from "@mui/icons-material/Delete";
import api from "../../../utils/api";

/**
 * CommentModal Component
 *
 * Allows adding comments + uploading multiple attachments per entry.
 * ✅ Automatically triggers parent refresh (reloads comments tab) after save.
 */
const CommentModal = ({
  open,
  onClose,
  workorderId,
  uploadedBy,
  commentType,
  setCommentType,
  newComment,
  setNewComment,
  onCommentAdded, // ✅ new callback prop to refresh parent
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // 🧩 Dropzone for multiple file uploads
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [],
      "application/pdf": [],
    },
    multiple: true,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const newFiles = [...selectedFiles, ...acceptedFiles];
        const newPreviews = [
          ...filePreviews,
          ...acceptedFiles.map((f) => ({
            name: f.name,
            type: f.type,
            preview: URL.createObjectURL(f),
          })),
        ];
        setSelectedFiles(newFiles);
        setFilePreviews(newPreviews);
      }
    },
  });

  const handleRemoveFile = (index) => {
    const updatedFiles = [...selectedFiles];
    const updatedPreviews = [...filePreviews];
    URL.revokeObjectURL(updatedPreviews[index].preview);
    updatedFiles.splice(index, 1);
    updatedPreviews.splice(index, 1);
    setSelectedFiles(updatedFiles);
    setFilePreviews(updatedPreviews);
  };

  // 💾 Save handler (comment + multiple files)
  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      // Step 1️⃣: Save the comment
      const commentPayload = {
        work_order_id: workorderId,
        comment_text: newComment.trim(),
        is_internal: commentType === "internal" ? 1 : 0,
      };

      const commentRes = await api.post("/addComment", commentPayload);
      const commentId = commentRes.data?.comment_id;

      // Step 2️⃣: Upload all selected files
      if (selectedFiles.length > 0) {
        const formData = new FormData();
        formData.append("workorder_id", workorderId);
        formData.append("uploaded_by", uploadedBy || 1);
        if (commentId) formData.append("comment_id", commentId);

        selectedFiles.forEach((file) => {
          formData.append("upload_urls", file);
        });

        await api.post("/workorder-files", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // ✅ Success feedback
      setSnackbar({
        open: true,
        message: "Comment and files uploaded successfully!",
        severity: "success",
      });

      // 🔄 Trigger parent refresh
      if (onCommentAdded) onCommentAdded();

      // Reset all states
      setNewComment("");
      setCommentType("external");
      setSelectedFiles([]);
      setFilePreviews([]);

      // Close modal
      onClose();
    } catch (error) {
      console.error("❌ Error saving comment:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message ||
          "Failed to save comment or upload files.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            backgroundColor: "white",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Add Comment
          </Typography>

          {/* Comment text area */}
          <TextField
            fullWidth
            label="Comment"
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Comment type */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Comment Type</InputLabel>
            <Select
              value={commentType}
              onChange={(e) => setCommentType(e.target.value)}
            >
              <MenuItem value="external">External</MenuItem>
              <MenuItem value="internal">Internal</MenuItem>
            </Select>
          </FormControl>

          {/* File upload area */}
          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: "8px",
              p: 2.5,
              textAlign: "center",
              cursor: "pointer",
              mb: 2,
              "&:hover": { backgroundColor: "#f9f9f9" },
            }}
          >
            <input {...getInputProps()} />
            <Typography variant="body2" color="text.secondary">
              Drag & drop files, or click to select (multiple supported)
            </Typography>
          </Box>

          {/* Previews grid */}
          {filePreviews.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                justifyContent: "center",
                mb: 2,
              }}
            >
              {filePreviews.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    width: 90,
                    height: 90,
                    border: "1px solid #ddd",
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#fafafa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {file.type.includes("image") ? (
                    <img
                      src={file.preview}
                      alt={file.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        p: 1,
                        textAlign: "center",
                        color: "#666",
                        fontSize: "11px",
                      }}
                    >
                      {file.name}
                    </Typography>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      background: "rgba(255,255,255,0.7)",
                      "&:hover": { background: "rgba(255,255,255,0.9)" },
                    }}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {/* Save button */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSaving || !newComment.trim()}
            startIcon={
              isSaving ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {isSaving ? "Saving..." : "Save Comment"}
          </Button>
        </Box>
      </Modal>

      {/* Snackbar feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default CommentModal;
